
import { useEffect, useState } from 'react';
import { useSubdomain } from '@/hooks/use-subdomain';

interface WhiteLabelConfig {
  enableWhiteLabel?: boolean;
  logo?: string;
  primaryColor?: string;
  secondaryColor?: string;
  companyName?: string;
  customCss?: string;
  hideOriginalBranding?: boolean;
  customFooter?: string;
  customHeader?: string;
  favicon?: string;
  metaTitle?: string;
  metaDescription?: string;
}

export function CompleteWhiteLabelBranding() {
  const { organization, isMainDomain } = useSubdomain();
  const [config, setConfig] = useState<WhiteLabelConfig>({});

  useEffect(() => {
    if (!isMainDomain && organization?.branding) {
      const branding = organization.branding as WhiteLabelConfig;
      
      // Only apply white-label if enabled
      if (branding.enableWhiteLabel) {
        setConfig(branding);
        
        // Apply custom CSS variables for branding
        const root = document.documentElement;
        if (branding.primaryColor) {
          root.style.setProperty('--brand-primary', branding.primaryColor);
          root.style.setProperty('--cso-600', branding.primaryColor);
          root.style.setProperty('--cso-700', adjustColor(branding.primaryColor, -20));
        }
        if (branding.secondaryColor) {
          root.style.setProperty('--brand-secondary', branding.secondaryColor);
        }
        
        // Update page title and meta
        if (branding.companyName) {
          document.title = branding.metaTitle || `${branding.companyName} - Strategic Planning Platform`;
        }
        
        // Update meta description
        if (branding.metaDescription) {
          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc) {
            metaDesc.setAttribute('content', branding.metaDescription);
          }
        }
        
        // Update favicon if provided
        if (branding.favicon) {
          const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
          if (favicon) {
            favicon.href = branding.favicon;
          }
        }
      }
    }
  }, [organization, isMainDomain]);

  // Only render on subdomains with white-label enabled
  if (isMainDomain || !organization || !config.enableWhiteLabel) {
    return null;
  }

  return (
    <>
      {/* Inject comprehensive custom styles */}
      <style>{`
        :root {
          --brand-primary: ${config.primaryColor || '#3b82f6'};
          --brand-secondary: ${config.secondaryColor || '#64748b'};
          --cso-600: ${config.primaryColor || '#3b82f6'};
          --cso-700: ${adjustColor(config.primaryColor || '#3b82f6', -20)};
        }
        
        /* Apply brand colors throughout the application */
        .brand-primary { color: var(--brand-primary) !important; }
        .bg-brand-primary { background-color: var(--brand-primary) !important; }
        .border-brand-primary { border-color: var(--brand-primary) !important; }
        .hover\\:bg-brand-primary:hover { background-color: var(--brand-primary) !important; }
        
        /* Override CSO colors with brand colors */
        .bg-cso-600 { background-color: var(--brand-primary) !important; }
        .hover\\:bg-cso-700:hover { background-color: var(--cso-700) !important; }
        .text-cso-600 { color: var(--brand-primary) !important; }
        .border-cso-600 { border-color: var(--brand-primary) !important; }
        
        /* Button variants using brand colors */
        .btn-primary { background-color: var(--brand-primary) !important; }
        .btn-primary:hover { background-color: var(--cso-700) !important; }
        
        /* Additional brand overrides */
        .bg-blue-600 { background-color: var(--brand-primary) !important; }
        .hover\\:bg-blue-700:hover { background-color: var(--cso-700) !important; }
        .text-blue-600 { color: var(--brand-primary) !important; }
        
        /* Hide original branding if requested */
        ${config.hideOriginalBranding ? `
          .original-branding,
          [data-original-branding],
          [alt*="CSO Agent"],
          [href*="csoagent.com"]:not([href*="${organization.subdomain}"]) {
            display: none !important;
          }
          
          /* Replace "CSO Agent" text */
          *:not(script):not(style) {
            font-family: inherit;
          }
        ` : ''}
        
        /* Custom CSS from organization */
        ${config.customCss || ''}
        
        /* Responsive logo sizing */
        .white-label-logo img {
          max-height: 40px;
          width: auto;
        }
        
        @media (max-width: 768px) {
          .white-label-logo img {
            max-height: 32px;
          }
        }
      `}</style>
      
      {/* Custom header */}
      {config.customHeader && (
        <div 
          className="custom-header" 
          dangerouslySetInnerHTML={{ __html: config.customHeader }}
        />
      )}
      
      {/* Replace navbar logo */}
      {config.logo && (
        <style>{`
          /* Hide original navbar logo and replace with custom */
          .navbar-logo { display: none !important; }
          
          /* Inject custom logo */
          .navbar::before {
            content: '';
            background-image: url('${config.logo}');
            background-size: contain;
            background-repeat: no-repeat;
            background-position: center;
            width: 120px;
            height: 40px;
            display: inline-block;
          }
        `}</style>
      )}
      
      {/* Custom footer */}
      {config.customFooter && (
        <div 
          className="custom-footer" 
          dangerouslySetInnerHTML={{ __html: config.customFooter }}
        />
      )}
    </>
  );
}

// Helper function to adjust color brightness
function adjustColor(color: string, amount: number): string {
  const usePound = color[0] === '#';
  const col = usePound ? color.slice(1) : color;
  const num = parseInt(col, 16);
  let r = (num >> 16) + amount;
  let g = (num >> 8 & 0x00FF) + amount;
  let b = (num & 0x0000FF) + amount;
  r = r > 255 ? 255 : r < 0 ? 0 : r;
  g = g > 255 ? 255 : g < 0 ? 0 : g;
  b = b > 255 ? 255 : b < 0 ? 0 : b;
  return (usePound ? '#' : '') + (r << 16 | g << 8 | b).toString(16).padStart(6, '0');
}
